import { useRef, useState } from "react";
import { Button, Card, CardProps } from "antd";
import { DownOutlined } from "@ant-design/icons";

import { Mask } from "../../types/masks";

import { getHistoryToShow, getOccurrencesSorted } from "./utils";
import { generateRandomId } from "../../utils/common";

import FeedbackAction from "./FeedbackAction";
import FeedbackCardOccurrences from "./FeedbackCardOccurrences";

import styles from "./FeedbackCard.module.css";
import { CriteriaOption } from "../../types/task";
import CriteriaTag from "../Tag/CriteriaTag";

interface FeedbackCardProps extends CardProps {
  maskInfo: Mask;
  selected: boolean;
  goToAnnotation: (maskId: number, occurrenceId: number) => void;
  onSelect?: () => void;
}

const FeedbackCard = ({
  maskInfo,
  selected,
  goToAnnotation,
  onSelect,
  ...rest
}: FeedbackCardProps) => {
  const feedbackRef = useRef<HTMLParagraphElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  const [closedChevron, setClosedChevron] = useState(true);

  const toggleCard = () => setClosedChevron(!closedChevron);

  const occurrences = getOccurrencesSorted(maskInfo);

  const historyToShow = getHistoryToShow(closedChevron, maskInfo.history);

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (onSelect) {
      onSelect();
    }
    
    if (occurrences.length > 0) {
      const firstOccurrence = occurrences[0];
      goToAnnotation(maskInfo.id, firstOccurrence.id);
    }
  };

  return (
    <Card
      className={`feedbackCard ${selected && "selected"}`}
      actions={historyToShow.map((action) => (
        <FeedbackAction
          key={generateRandomId()}
          action={action}
          occurrences={maskInfo.occurrences}
        />
      ))}
      onClick={handleCardClick}
      {...rest}
    >
      {maskInfo.criteria && <header className={styles.feedbackCardHeader}>
        <CriteriaTag criteria={maskInfo.criteria as CriteriaOption} />
        <Button
          onClick={toggleCard}
          type="text"
          icon={
            <DownOutlined
              className={`rotate ${!closedChevron && "up"}`}
              ref={iconRef}
            />
          }
        />
      </header>}
      <section className={styles.feedbackSection}>
        <h4 className={`lg strong m-0 ${closedChevron && "ellipsed"} one-line`}>
          {maskInfo.name}
        </h4>
        <FeedbackCardOccurrences
          maskInfo={maskInfo}
          occurrences={occurrences}
          goToAnnotation={goToAnnotation}
        />
        {maskInfo.action_reasoning && <p
          ref={feedbackRef}
          className={`lg normal m-0 ${closedChevron && "ellipsed"} two-lines`}
        >
          {maskInfo.action_reasoning}
        </p>
        }
      </section>
    </Card>
  );
};
export default FeedbackCard;
